import { useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdClose, MdContentCopy } from 'react-icons/md';

// Bouton « Dupliquer » de l'onglet Matching : applique le worktype (et la note) d'un produit leadrs
// à plusieurs autres produits d'un coup — plusieurs produits leadrs vers un seul métier Batiweb.
// Aussi utilisée par l'onglet « À matcher » : la source est alors un worktype seul (subIndustryId null).

export interface MappingRow {
    industry: string;
    subIndustry: string;
    subIndustryId: number | null;
    worktypeId: number | null;
    worktypeLabel: string | null;
    note: string;
    updatedBy: string | null;
    updatedAt: string | null;
}

interface Props {
    source: MappingRow;
    rows: MappingRow[];
    onClose: () => void;
    onApplied: (saved: MappingRow[]) => void;
}

const targetLabel = (row: MappingRow) =>
    row.worktypeId != null ? `${row.worktypeLabel} (${row.worktypeId})` : row.updatedAt ? "Pas d'équivalent" : 'À paramétrer';

export default function DuplicateMappingModal({ source, rows, onClose, onApplied }: Props) {
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState('');
    const [copyNote, setCopyNote] = useState(true);
    const fromWorktype = source.subIndustryId == null;
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Toutes les sous-industries paramétrables, sauf la source, groupées par industrie.
    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const candidates = rows.filter(
            (row) =>
                row.subIndustryId != null &&
                row.subIndustryId !== source.subIndustryId &&
                (!q || row.subIndustry.toLowerCase().includes(q) || row.industry.toLowerCase().includes(q))
        );
        const byIndustry = new Map<string, MappingRow[]>();
        for (const row of candidates) byIndustry.set(row.industry, [...(byIndustry.get(row.industry) || []), row]);
        // Industrie de la source en premier : c'est là qu'on duplique le plus souvent.
        return [...byIndustry.entries()].sort(([a], [b]) => (a === source.industry ? -1 : b === source.industry ? 1 : a.localeCompare(b, 'fr')));
    }, [rows, source, search]);

    const toggle = (id: number) =>
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleGroup = (groupRows: MappingRow[]) => {
        const ids = groupRows.map((r) => r.subIndustryId as number);
        const allSelected = ids.every((id) => selected.has(id));
        setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
            return next;
        });
    };

    // Produits déjà mappés sur un autre worktype : leur correspondance va être écrasée.
    const overwritten = rows.filter(
        (row) => row.subIndustryId != null && selected.has(row.subIndustryId) && row.updatedAt && row.worktypeId !== source.worktypeId
    );

    const handleApply = async () => {
        setError('');
        try {
            setSaving(true);
            const res = await axios.put(
                `${CONFIG.apiUrl}/admin/batiweb/matching`,
                { subIndustryIds: [...selected], worktypeId: source.worktypeId, note: copyNote ? source.note : '' },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            onApplied(res.data.mappings);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur lors de la duplication.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative z-10 m-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-navy-800">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                    <div>
                        <h2 className="text-lg font-bold text-navy-700 dark:text-white">{fromWorktype ? 'Matcher le worktype' : 'Dupliquer le matching'}</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Appliquer <span className="font-semibold text-navy-700 dark:text-white">{targetLabel(source)}</span>
                            {!fromWorktype && <> (comme pour « {source.subIndustry} »)</>} aux produits leadrs cochés.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <MdClose size={22} />
                    </button>
                </div>

                <div className="border-b border-gray-200 px-6 py-3 dark:border-navy-600">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un produit ou une industrie"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-3">
                    {groups.length === 0 && <div className="py-6 text-center text-sm text-gray-400">Aucun produit.</div>}
                    {groups.map(([industry, groupRows]) => {
                        const allSelected = groupRows.every((r) => selected.has(r.subIndustryId as number));
                        return (
                            <div key={industry} className="mb-4">
                                <div className="mb-1 flex items-center justify-between">
                                    <div className="text-xs font-bold uppercase text-gray-500">{industry}</div>
                                    <button onClick={() => toggleGroup(groupRows)} className="text-xs font-semibold text-brand-500 hover:underline">
                                        {allSelected ? 'Tout décocher' : 'Tout cocher'}
                                    </button>
                                </div>
                                {groupRows.map((row) => {
                                    const id = row.subIndustryId as number;
                                    const same = row.updatedAt && row.worktypeId === source.worktypeId;
                                    return (
                                        <label key={id} className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-navy-700">
                                            <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} />
                                            <span className="flex-1 text-navy-700 dark:text-white">{row.subIndustry}</span>
                                            <span className={`text-xs ${same ? 'text-green-600' : row.updatedAt ? 'text-gray-500' : 'text-orange-600'}`}>
                                                {same ? 'déjà identique' : targetLabel(row)}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                <div className="border-t border-gray-200 px-6 py-4 dark:border-navy-600">
                    {overwritten.length > 0 && (
                        <div className="mb-3 rounded-lg bg-orange-50 p-2 text-xs text-orange-700">
                            {overwritten.length} produit(s) déjà paramétré(s) autrement seront écrasés : {overwritten.map((r) => r.subIndustry).join(', ')}
                        </div>
                    )}
                    {error && <div className="mb-3 rounded-lg bg-red-100 p-2 text-sm text-red-700">{error}</div>}
                    <div className="flex items-center justify-between gap-4">
                        {fromWorktype ? (
                            <span />
                        ) : (
                            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                <input type="checkbox" checked={copyNote} onChange={(e) => setCopyNote(e.target.checked)} disabled={!source.note} />
                                Copier aussi la note{source.note ? '' : ' (vide)'}
                            </label>
                        )}
                        <button
                            onClick={handleApply}
                            disabled={selected.size === 0 || saving}
                            className="flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                        >
                            <MdContentCopy />
                            {saving ? 'Application...' : `Appliquer à ${selected.size} produit(s)`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
